import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, X, Minus, Plus,
  ShoppingBag, Share2, Check, Ruler, MessageCircle,
  ChevronDown, Truck, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
import { getCloudinaryMedium, getCloudinaryThumbnail } from '../lib/cloudinaryUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { trackProductView } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_MAP = {
  'Negro': '#000000',
  'Blanco': '#FFFFFF',
  'Dorado': '#C9A962',
  'Verde': '#2D5A27',
  'Verde Aguacate': '#8DB600',
  'Coral': '#FF6F61',
  'Natural': '#E8D5B7',
  'Rojo': '#C41E3A',
  'Azul': '#1B3A5C',
  'Rosa': '#F4A7BB',
  'Beige': '#D4B896',
  'Marron': '#6B3A2A',
};

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem, openCart } = useCart();
  const galleryRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showDescription, setShowDescription] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${slug}`);
        setProduct(res.data);
        trackProductView(window.location.pathname, res.data.id, res.data.name, res.data.category_name);
        if (res.data.variants && res.data.variants.length > 0) {
          const av = res.data.variants.find(v => v.stock_quantity > 0);
          if (av) { setSelectedSize(av.size); setSelectedColor(av.color); }
        }
        if (res.data.category_id) {
          const rr = await axios.get(`${API}/products?category_id=${res.data.category_id}&status=active&limit=5`);
          setRelatedProducts(rr.data.filter(p => p.id !== res.data.id).slice(0, 4));
        }
      } catch (e) {} finally { setLoading(false); }
    };
    fetchProduct();
    setCurrentImageIndex(0);
    setQuantity(1);
    setAddedToCart(false);
  }, [slug]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!isZoomed || !product?.images) return;
      if (e.key === 'ArrowRight') setCurrentImageIndex(p => (p + 1) % product.images.length);
      if (e.key === 'ArrowLeft') setCurrentImageIndex(p => (p - 1 + product.images.length) % product.images.length);
      if (e.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  // Horizontal scroll snap detection for mobile gallery
  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const handleScroll = () => {
      const idx = Math.round(el.scrollLeft / el.offsetWidth);
      setCurrentImageIndex(idx);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
          <div className="grid lg:grid-cols-[1fr,420px] gap-8">
            <div className="aspect-[3/4] rounded-2xl skeleton" />
            <div className="space-y-4 py-4">
              <div className="h-4 skeleton w-1/3 rounded" />
              <div className="h-8 skeleton w-3/4 rounded" />
              <div className="h-6 skeleton w-1/4 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-white mb-3">Producto no encontrado</h1>
          <Link to="/tienda" className="text-gold text-sm">← Volver a la tienda</Link>
        </div>
      </div>
    );
  }

  const sizes = [...new Set(product.variants?.map(v => v.size) || [])];
  const colors = [...new Set(product.variants?.map(v => v.color) || [])];
  const currentVariant = product.variants?.find(v => v.size === selectedSize && v.color === selectedColor);
  const stockQuantity = currentVariant?.stock_quantity || 0;
  const isOutOfStock = stockQuantity === 0;
  const isLowStock = stockQuantity > 0 && stockQuantity <= 5;
  const discount = product.compare_price ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : null;
  const images = product.images && product.images.length > 0 ? product.images : [];

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) { toast.error('Selecciona talla y color'); return; }
    if (isOutOfStock) { toast.error('Producto agotado'); return; }
    addItem(product, { size: selectedSize, color: selectedColor, stock_quantity: stockQuantity }, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
    toast.success(`Agregado al carrito`);
    openCart();
  };

  const scrollToImage = (idx) => {
    if (galleryRef.current) {
      galleryRef.current.scrollTo({ left: idx * galleryRef.current.offsetWidth, behavior: 'smooth' });
    }
    setCurrentImageIndex(idx);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="product-page">

      {/* Mobile: Full-width horizontal scroll gallery */}
      <div className="lg:hidden">
        <div className="relative">
          {/* Floating buttons over gallery */}
          <div className="absolute top-4 left-0 right-0 z-20 flex items-center justify-between px-4">
            <button
              onClick={() => window.history.back()}
              className="w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-md rounded-full text-white active:scale-95 transition-transform"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Enlace copiado'); }}
              className="w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-md rounded-full text-white active:scale-95 transition-transform"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>

          {/* Horizontal scroll images */}
          <div
            ref={galleryRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide mt-[88px]"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {images.length > 0 ? images.map((img, i) => (
            <div key={i} className="flex-shrink-0 w-full snap-center">
              <div className="aspect-[3/4] bg-[#111]">
                <img
                  src={getCloudinaryMedium(img)}
                  alt={`${product.name} ${i + 1}`}
                  className="w-full h-full object-contain"
                  onClick={() => setIsZoomed(true)}
                />
              </div>
            </div>
          )) : (
            <div className="flex-shrink-0 w-full">
              <div className="aspect-[3/4] bg-[#111] flex items-center justify-center text-white/10">
                <ShoppingBag className="w-16 h-16" />
              </div>
            </div>
          )}
        </div>

          {/* Discount badge inside gallery */}
          {discount && (
            <div className="absolute top-4 left-16 z-10">
              <span className="bg-gold text-black px-2.5 py-1 text-xs font-bold rounded-full">-{discount}%</span>
            </div>
          )}
        </div>

        {/* Image dots */}
        {images.length > 1 && (
          <div className="flex justify-center gap-1.5 py-3">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToImage(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === currentImageIndex ? 'bg-gold w-6' : 'bg-white/20 w-1.5'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Desktop: Two column layout */}
      <div className="hidden lg:block pt-20">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-1 text-white/30 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>

          <div className="grid grid-cols-[1fr,400px] gap-10">
            {/* Desktop gallery - masonry style */}
            <div className="space-y-3">
              {images.length > 0 ? (
                <>
                  {/* Main large image */}
                  <div
                    className="relative aspect-[3/4] bg-[#111] rounded-2xl overflow-hidden cursor-zoom-in group"
                    onClick={() => setIsZoomed(true)}
                  >
                    <img
                      src={getCloudinaryMedium(images[currentImageIndex])}
                      alt={product.name}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                    {discount && (
                      <span className="absolute top-4 left-4 bg-gold text-black px-3 py-1 text-xs font-bold rounded-full">-{discount}%</span>
                    )}
                  </div>

                  {/* Thumbnail row */}
                  {images.length > 1 && (
                    <div className="flex gap-2">
                      {images.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          className={`relative w-20 h-24 rounded-xl overflow-hidden transition-all duration-200 ${
                            i === currentImageIndex ? 'ring-2 ring-gold opacity-100' : 'opacity-40 hover:opacity-70'
                          }`}
                        >
                          <img src={getCloudinaryThumbnail(img)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-[3/4] bg-[#111] rounded-2xl flex items-center justify-center text-white/10">
                  <ShoppingBag className="w-20 h-20" />
                </div>
              )}
            </div>

            {/* Desktop product info - sticky */}
            <div className="lg:sticky lg:top-24 lg:self-start space-y-5">
              {renderProductInfo()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile product info */}
      <div className="lg:hidden px-4 pb-8">
        <div className="space-y-5 pt-2">
          {renderProductInfo()}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 lg:py-16">
          <h2 className="text-lg font-semibold text-white mb-5">Completa tu look</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {relatedProducts.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {isZoomed && images.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black"
          >
            <button
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 rounded-full text-white z-10"
              onClick={() => setIsZoomed(false)}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="h-full flex items-center justify-center">
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(p => (p - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 rounded-full text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex(p => (p + 1) % images.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 rounded-full text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md rounded-full px-3 py-1.5 text-white/70 text-xs">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  function renderProductInfo() {
    return (
      <>
        {/* Category */}
        {product.category_name && (
          <Link to={`/tienda/${product.category_id}`} className="text-white/30 text-xs uppercase tracking-wider hover:text-gold transition-colors">
            {product.category_name}
          </Link>
        )}

        {/* Name */}
        <h1 className="text-2xl md:text-3xl font-serif text-white leading-snug -mt-2">{product.name}</h1>

        {/* Price */}
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-semibold text-white">${product.price.toFixed(2)}</span>
          {product.compare_price && (
            <>
              <span className="text-white/25 line-through text-base">${product.compare_price.toFixed(2)}</span>
              <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-0.5 rounded-full">-{discount}%</span>
            </>
          )}
        </div>

        {/* Stock pill */}
        {isOutOfStock ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-red-400 bg-red-500/10 px-3 py-1.5 rounded-full">Agotado</span>
        ) : isLowStock ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-orange-400 bg-orange-500/10 px-3 py-1.5 rounded-full">¡Últimas {stockQuantity}!</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            Disponible
          </span>
        )}

        {/* Color Selector — swatch for known colors, readable chip for custom ones */}
        {colors.length > 0 && (
          <div>
            <span className="text-white/50 text-xs mb-2.5 block">Color: <span className="text-white">{(selectedColor || '').trim()}</span></span>
            <div className="flex flex-wrap gap-2.5 items-center">
              {colors.map((color) => {
                const colorHasStock = product.variants.some(v => v.color === color && v.stock_quantity > 0);
                const isSelected = selectedColor === color;
                const label = (color || '').trim() || 'Único';
                const swatch = COLOR_MAP[label];

                const selectColor = () => {
                  setSelectedColor(color);
                  const exact = product.variants.find(v => v.color === color && v.size === selectedSize);
                  if (!exact || exact.stock_quantity === 0) {
                    const av = product.variants.find(v => v.color === color && v.stock_quantity > 0)
                      || product.variants.find(v => v.color === color);
                    if (av) setSelectedSize(av.size);
                  }
                };

                if (swatch) {
                  const isLight = ['Blanco', 'Natural', 'Beige', 'Dorado'].includes(label);
                  return (
                    <button
                      key={color}
                      onClick={selectColor}
                      title={label}
                      className={`relative w-9 h-9 rounded-full transition-all duration-200 cursor-pointer hover:scale-110 ${
                        isSelected ? 'ring-2 ring-offset-2 ring-offset-black ring-gold scale-110' : ''
                      } ${!colorHasStock ? 'opacity-50' : ''}`}
                      style={{ backgroundColor: swatch }}
                    >
                      {isSelected && (
                        <Check className={`w-4 h-4 absolute inset-0 m-auto ${isLight ? 'text-black' : 'text-white'}`} />
                      )}
                    </button>
                  );
                }

                return (
                  <button
                    key={color}
                    onClick={selectColor}
                    title={label}
                    className={`px-3 h-9 rounded-full text-xs font-medium border transition-all duration-200 ${
                      isSelected
                        ? 'bg-white text-black border-white'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                    } ${!colorHasStock ? 'opacity-60' : ''}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Size Selector */}
        {sizes.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-white/50 text-xs">Talla</span>
              <Dialog>
                <DialogTrigger className="text-white/30 text-xs flex items-center gap-1 hover:text-gold transition-colors">
                  <Ruler className="w-3 h-3" />
                  Guía
                </DialogTrigger>
                <DialogContent className="bg-[#111] border-white/10 text-white max-w-md rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-lg">Guía de Tallas</DialogTitle>
                  </DialogHeader>
                  <table className="w-full text-sm mt-3">
                    <thead>
                      <tr className="border-b border-white/10 text-white/40 text-xs">
                        <th className="py-2 text-left">Talla</th>
                        <th className="py-2 text-center">Busto</th>
                        <th className="py-2 text-center">Cintura</th>
                        <th className="py-2 text-center">Cadera</th>
                      </tr>
                    </thead>
                    <tbody className="text-white/60 text-xs">
                      {[['XS','80-84','60-64','86-90'],['S','84-88','64-68','90-94'],['M','88-92','68-72','94-98'],['L','92-96','72-76','98-102'],['XL','96-100','76-80','102-106']].map(([s,b,c,h]) => (
                        <tr key={s} className="border-b border-white/5">
                          <td className="py-2 text-gold">{s}</td>
                          <td className="py-2 text-center">{b}</td>
                          <td className="py-2 text-center">{c}</td>
                          <td className="py-2 text-center">{h}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => {
                const sizeHasStock = product.variants.some(v => v.size === size && v.stock_quantity > 0);
                const isSelected = selectedSize === size;

                return (
                  <button
                    key={size}
                    onClick={() => {
                      setSelectedSize(size);
                      const exact = product.variants.find(v => v.size === size && v.color === selectedColor);
                      if (!exact || exact.stock_quantity === 0) {
                        const av = product.variants.find(v => v.size === size && v.stock_quantity > 0)
                          || product.variants.find(v => v.size === size);
                        if (av) setSelectedColor(av.color);
                      }
                    }}
                    className={`min-w-[44px] h-10 px-3 rounded-full text-sm font-medium transition-all duration-200 ${
                      isSelected
                        ? 'bg-white text-black'
                        : sizeHasStock
                          ? 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                          : 'bg-white/5 text-white/40 hover:bg-white/10 border border-dashed border-white/15'
                    }`}
                  >
                    {(size || '').trim() || 'Única'}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add to Cart */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white/5 rounded-full border border-white/10 h-12">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="px-4 h-full text-white/50 hover:text-white transition-colors">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-2 text-white font-medium min-w-[28px] text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(q => Math.min(stockQuantity, q + 1))}
                disabled={quantity >= stockQuantity}
                className="px-4 h-full text-white/50 hover:text-white transition-colors disabled:opacity-30"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <motion.button
              onClick={handleAddToCart}
              disabled={isOutOfStock}
              whileTap={{ scale: 0.97 }}
              className={`flex-1 h-12 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                addedToCart
                  ? 'bg-emerald-500 text-white'
                  : isOutOfStock
                    ? 'bg-white/5 text-white/30 cursor-not-allowed'
                    : 'bg-gold hover:bg-gold-hover text-black'
              }`}
            >
              {addedToCart ? (
                <><Check className="w-4 h-4" /> Agregado</>
              ) : isOutOfStock ? (
                'Agotado'
              ) : (
                <><ShoppingBag className="w-4 h-4" /> Agregar al carrito · ${(product.price * quantity).toFixed(2)}</>
              )}
            </motion.button>
          </div>

          <a
            href={`https://wa.me/584242925792?text=${encodeURIComponent(`Hola! Me interesa: ${product.name}${selectedSize ? ` - Talla: ${selectedSize.trim()}` : ''}${selectedColor ? ` - Color: ${selectedColor.trim()}` : ''}\n\n${window.location.href}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 h-11 rounded-full text-[#25D366] hover:bg-[#25D366]/10 text-sm font-medium transition-colors w-full"
          >
            <MessageCircle className="w-4 h-4" />
            Consultar por WhatsApp
          </a>
        </div>

        {/* Description accordion */}
        {product.description && (
          <button
            onClick={() => setShowDescription(!showDescription)}
            className="w-full flex items-center justify-between py-3 border-t border-white/5 text-sm text-white/60 hover:text-white transition-colors"
          >
            <span>Descripción</span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDescription ? 'rotate-180' : ''}`} />
          </button>
        )}
        <AnimatePresence>
          {showDescription && product.description && (
            <motion.p
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="text-white/40 text-sm leading-relaxed overflow-hidden -mt-3"
            >
              {product.description}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Trust badges */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          {[
            { icon: Truck, text: 'Envío nacional' },
            { icon: Shield, text: 'Pago seguro' },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex flex-col items-center gap-1.5 py-3 bg-white/[0.02] rounded-xl">
              <Icon className="w-4 h-4 text-white/20" />
              <span className="text-[10px] text-white/25 text-center leading-tight">{text}</span>
            </div>
          ))}
        </div>

        {/* Share - desktop */}
        <div className="hidden lg:flex items-center gap-3 pt-1">
          <button
            onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Enlace copiado'); }}
            className="flex items-center gap-1.5 text-white/20 hover:text-white/40 transition-colors text-xs"
          >
            <Share2 className="w-3 h-3" />
            Compartir
          </button>
        </div>
      </>
    );
  }
}
